import { IQuotationRoute } from "interfaces/quotation-detail.interface";
import React from "react";
import { Card, CardBody, CardTitle, Badge, Row, Col } from "reactstrap";
import { Clock, GeoAlt, Truck, Calendar, CurrencyDollar } from "react-bootstrap-icons";
import BPlantColor from "../batchingPlant";
import { formatIDR } from "helper";

export default function ApprovalRoute({ data }: { data: IQuotationRoute[] }) {
	console.log("ApprovalRoute data:", data);

	const formatDistance = (distance: number) => {
		if (distance >= 1000) {
			return `${(distance / 1000).toFixed(1)} km`;
		}
		return `${distance} m`;
	};

	const formatDuration = (duration: number) => {
		const hours = Math.floor(duration / 3600);
		const minutes = Math.floor((duration % 3600) / 60);

		if (hours > 0) {
			return `${hours}h ${minutes}m`;
		}
		return `${minutes}m`;
	};

	return (
		<Card className="mt-3 shadow-sm">
			<CardBody className="p-3">
				<CardTitle
					tag="h6"
					className="mb-3 d-flex align-items-center justify-content-between"
					style={{ fontSize: "0.95rem", fontWeight: "600" }}
				>
					<span className="text-dark">Rute Approval</span>
					<div className="break-line" />
				</CardTitle>
				{data && data.length > 0 ? (
					<div className="approval-routes">
						{data.map((route, index) => (
							<div
								key={route.id || index}
								className="route-card p-3 mb-3 border rounded"
								style={{
									backgroundColor: "#f8f9fa",
									borderColor: "#e9ecef",
									transition: "all 0.2s ease"
								}}
							>
								{/* Header with Plant Name and Status */}
								<div className="d-flex justify-content-between align-items-center mb-3">
									<div className="d-flex align-items-center">
										<div
											className="icon-wrapper me-2 d-flex align-items-center justify-content-center"
											style={{
												width: "32px",
												height: "32px",
												backgroundColor: "#e3f2fd",
												borderRadius: "50%"
											}}
										>
											<Truck
												className="text-primary"
												size={16}
											/>
										</div>
										<div>
											<BPlantColor>
												{route.batchingPlant
													?.displayName ||
													route.batchingPlant?.name ||
													"Unknown Plant"}
											</BPlantColor>
										</div>
									</div>
									{route.isSurveyed && (
										<Badge
											color="success"
											pill
											className="px-2 py-1"
											style={{ fontSize: "0.7rem" }}
										>
											✓ Surveyed
										</Badge>
									)}
								</div>
								{/* Route Information Grid */}
								<div className="route-info mb-3">
									<Row className="g-3">
										<Col xs={4}>
											<div className="info-item d-flex align-items-center">
												<div
													className="info-icon me-2"
													style={{
														width: "24px",
														height: "24px",
														backgroundColor:
															"#fff3e0",
														borderRadius: "4px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center"
													}}
												>
													<GeoAlt
														size={12}
														className="text-warning"
													/>
												</div>
												<div>
													<div className="text-muted small">
														Distance
													</div>
													<div
														className="fw-semibold"
														style={{
															fontSize: "0.85rem"
														}}
													>
														{formatDistance(
															route.route
																?.distance || 0
														)}
													</div>
												</div>
											</div>
										</Col>
										<Col xs={4}>
											<div className="info-item d-flex align-items-center">
												<div
													className="info-icon me-2"
													style={{
														width: "24px",
														height: "24px",
														backgroundColor:
															"#e8f5e8",
														borderRadius: "4px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center"
													}}
												>
													<Clock
														size={12}
														className="text-success"
													/>
												</div>
												<div>
													<div className="text-muted small">
														Duration
													</div>
													<div
														className="fw-semibold"
														style={{
															fontSize: "0.85rem"
														}}
													>
														{formatDuration(
															route.route
																?.duration || 0
														)}
													</div>
												</div>
											</div>
										</Col>
										<Col xs={4}>
											<div className="info-item d-flex align-items-center">
												<div
													className="info-icon me-2"
													style={{
														width: "24px",
														height: "24px",
														backgroundColor:
															"#e8ecf5",
														borderRadius: "4px",
														display: "flex",
														alignItems: "center",
														justifyContent: "center"
													}}
												>
													<CurrencyDollar
														size={12}
														className="text-success"
													/>
												</div>
												<div>
													<div className="text-muted small">
														Toll Fee
													</div>
													<div
														className="fw-semibold"
														style={{
															fontSize: "0.85rem"
														}}
													>
														{route?.tollFee ? `Rp ${formatIDR(route.tollFee)}` : "-"}
													</div>
												</div>
											</div>
										</Col>
									</Row>
								</div>

								{/* Action Buttons and Info */}
								<div className="d-flex justify-content-between align-items-center">
									<div className="route-actions">
										{route.gmapUrl && (
											<a
												href={route.gmapUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="btn btn-sm"
												style={{
													backgroundColor: "#1976d2",
													color: "white",
													border: "none",
													borderRadius: "6px",
													padding: "6px 12px",
													fontSize: "0.75rem",
													textDecoration: "none",
													display: "inline-flex",
													alignItems: "center"
												}}
											>
												<GeoAlt
													size={12}
													className="me-1"
												/>
												View Map
											</a>
										)}
									</div>
									{route.approvedAt && (
										<div className="approval-date d-flex align-items-center text-muted">
											<Calendar
												size={12}
												className="me-1"
											/>
											<span
												style={{ fontSize: "0.7rem" }}
											>
												{new Date(
													route.approvedAt
												).toLocaleDateString("id-ID")}
											</span>
										</div>
									)}
								</div>
							</div>
						))}
					</div>
				) : (
					<div className="empty-state text-center py-4">
						<div
							className="empty-icon mb-2 mx-auto"
							style={{
								width: "48px",
								height: "48px",
								backgroundColor: "#f5f5f5",
								borderRadius: "50%",
								display: "flex",
								alignItems: "center",
								justifyContent: "center"
							}}
						>
							<GeoAlt size={24} className="text-muted" />
						</div>
						<p
							className="text-muted mb-0"
							style={{ fontSize: "0.85rem" }}
						>
							No approval routes available
						</p>
					</div>
				)}
			</CardBody>
		</Card>
	);
}
